/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Flex,
  Input,
  Popover,
  Radio,
  Space,
  message,
} from "antd";
import {
  CaretDownOutlined,
  HistoryOutlined,
  MenuOutlined,
  OneToOneOutlined,
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import moment from "moment";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import { CustomTable } from "@/hocComponents";

const { RangePicker } = DatePicker;

/* ------------------------------------------------------------------ */
/* 常量                                                                 */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 25;
const TIME_FORMAT = "MM/DD/YYYY HH:mm";

const INITIAL_QUERY = {
  vendorName: null,
  propUnit: null,
  clockType: null,
  clockLongitude: null,
  clockLatitude: null,
  createByName: null,
  payAmount: null,
  sClockTime: null,
  eClockTime: null,
  orderField: null, // 排序字段
  orderType: null, // ascend / descend
};

// 列的公共属性
const BASE_COL = {
  hidden: false,
  align: "center",
  sorter: true,
  className: "custom-sort-header",
};

function SearchTitle({ label, field, store, onSearch }) {
  const [value, setValue] = useState(store.current[field] ?? "");

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    store.current[field] = next; // 输入过程中就同步到 store
    // 点击清除图标时（不是键盘输入），直接按空值重新查询
    if (next === "" && e.type === "click") {
      onSearch("");
    }
  };

  return (
    <div>
      <div>{label}</div>
      <div>
        <Input
          allowClear
          value={value}
          placeholder={`Search ${label}`}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()} // 避免点击输入框触发排序
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            e.stopPropagation();
            onSearch(e.target.value);
          }}
        />
      </div>
    </div>
  );
}

function TimeTitle({ store, onChange }) {
  const [value, setValue] = useState(store.current.clockTime ?? null);

  const handleChange = (dates) => {
    setValue(dates);
    store.current.clockTime = dates;
    onChange(dates);
  };

  return (
    <div>
      <div>Clock Time</div>
      <RangePicker
        value={value}
        format={TIME_FORMAT}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 页面                                                                 */
/* ------------------------------------------------------------------ */

export default function UserList() {
  const [tableList, setTableList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });

  const [visibleKeys, setVisibleKeys] = useState(null);
  const [resetKey, setResetKey] = useState(0); 

  const queryRef = useRef(INITIAL_QUERY);
  const searchStoreRef = useRef({}); 
  const pageSizeRef = useRef(PAGE_SIZE);
  const requestIdRef = useRef(0);

  /* ---------------------------- 请求数据 ---------------------------- */

  const fetchData = useCallback(
    async (page, pageSize, searchParams = queryRef.current) => {
      const requestId = ++requestIdRef.current;
      setTableLoading(true);
      try {
        const params = { page, count: pageSize };
        Object.keys(INITIAL_QUERY).forEach((key) => {
          params[key] = searchParams?.[key] || "";
        });

        const response = await http.get(api.getVendorClockShow, { params });
        if (requestId !== requestIdRef.current) return;
        const list = (response.data.list || []).map((item) => ({
          ...item,
          key: item.id,
        }));

        pageSizeRef.current = pageSize;
        setTableList(list);
        setPagination({
          current: page,
          pageSize,
          total: response.data.total || 0,
        });
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Fetch data error:", err);
        message.error("Failed to load data");
      } finally {
        if (requestId === requestIdRef.current) setTableLoading(false);
      }
    },
    []
  );

  const updateQuery = useCallback(
    (patch) => {
      const next = { ...queryRef.current, ...patch };
      queryRef.current = next;
      fetchData(1, pageSizeRef.current, next);
    },
    [fetchData]
  );

  // 首次加载
  useEffect(() => {
    fetchData(1, PAGE_SIZE);
  }, [fetchData]);

  /* ---------------------------- 事件处理 ---------------------------- */

  // 刷新：清空查询条件 + 清空搜索框内容
  const handleRefresh = () => {
    queryRef.current = INITIAL_QUERY;
    searchStoreRef.current = {};
    setResetKey((k) => k + 1);
    fetchData(1, PAGE_SIZE, INITIAL_QUERY);
  };

  // 时间范围（包括清空）
  const handleTimeChange = useCallback(
    (dates) => {
      updateQuery({
        sClockTime: dates?.[0]?.valueOf() ?? null,
        eClockTime: dates?.[1]?.valueOf() ?? null,
      });
    },
    [updateQuery]
  );

  // 分页 / 排序
  const handleTableChange = (pager, filters, sorter) => {
    const { field, order } = sorter || {};
    const next = {
      ...queryRef.current,
      orderField: field || null,
      orderType: order || null,
    };
    queryRef.current = next;
    fetchData(pager.current, pager.pageSize, next);
  };

  // 查看地图（新标签页打开）
  const handleViewMap = (record) => {
    const paramString = new URLSearchParams({
      ...record,
      _from: "vendorClock",
    }).toString();
    const hash = `/JobRequests/VendorClock/Map${
      paramString ? `?${paramString}` : ""
    }`;
    window.open(
      `${window.location.origin}#${hash}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /* ------------------------------ 列定义 ----------------------------- */

  // 带搜索框的列
  const searchColumn = (label, dataIndex, width) => ({
    ...BASE_COL,
    title: () => (
      <SearchTitle
        key={`${dataIndex}-${resetKey}`}
        label={label}
        field={dataIndex}
        store={searchStoreRef}
        onSearch={(value) => updateQuery({ [dataIndex]: value })}
      />
    ),
    label, // 给"列选择"弹层用的名字
    dataIndex,
    key: dataIndex,
    width,
  });

  const columns = useMemo(
    () => [
      {
        ...BASE_COL,
        sorter: false,
        label: "Action",
        title: "Action",
        key: "action",
        dataIndex: "#",
        width: 60,
        render: (_, record) =>
          commonFn.btnAuthorityFlag("ViewMap") ? (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "view",
                    icon: <HistoryOutlined />,
                    label: <div>View Map</div>,
                  },
                ],
                onClick: () => handleViewMap(record),
              }}
            >
              <MenuOutlined className="col-blue" />
            </Dropdown>
          ) : null,
      },
      searchColumn("Vendor Name", "vendorName", 110),
      searchColumn("Prop-Unit", "propUnit", 100),
      {
        ...searchColumn("Clock Type", "clockType", 100),
        render: (text) =>
          text == 1 ? "Repair" : text == 3 ? "Pre-Repair" : "",
      },
      {
        ...BASE_COL,
        label: "Clock Time",
        title: () => (
          <TimeTitle
            key={`clockTime-${resetKey}`}
            store={searchStoreRef}
            onChange={handleTimeChange}
          />
        ),
        dataIndex: "clockTime",
        key: "clockTime",
        width: 200,
        render: (text) =>
          text && !isNaN(new Date(text))
            ? moment(text).format(TIME_FORMAT)
            : "",
      },
      searchColumn("Longitude", "clockLongitude", 100),
      searchColumn("Latitude", "clockLatitude", 100),
      searchColumn("Create By Name", "createByName", 100),
    ],
    [resetKey, updateQuery, handleTimeChange]
  );

  const columnOptions = useMemo(
    () => columns.map((col) => ({ label: col.label, value: col.key })),
    [columns]
  );

  // 应用列显示/隐藏
  const displayColumns = useMemo(
    () =>
      visibleKeys
        ? columns.map((col) => ({
            ...col,
            hidden: !visibleKeys.includes(col.key),
          }))
        : columns,
    [columns, visibleKeys]
  );

  /* ------------------------------- 渲染 ------------------------------ */

  const columnSelector = (
    <Checkbox.Group
      className="checkBox"
      value={visibleKeys ?? columns.map((col) => col.key)}
      onChange={setVisibleKeys}
    >
      <Space direction="vertical">
        {columnOptions.map((opt) => (
          <Checkbox key={opt.value} value={opt.value}>
            {opt.label}
          </Checkbox>
        ))}
      </Space>
    </Checkbox.Group>
  );

  return (
    <div>
      <Flex className="topDiv" justify="space-between" align="flex-start">
        <div className="topLeft">
          {commonFn.btnAuthorityFlag("NewClockIn") && (
            <Space size="large">
              <Link target="_blank" to="/JobRequests/VendorClock/Add">
                <Button className="btnNew">
                  <PlusOutlined />
                  New Clock In
                </Button>
              </Link>
            </Space>
          )}
        </div>

        <Radio.Group defaultValue="a" size="middle">
          <Radio.Button value="a" onClick={handleRefresh}>
            <SyncOutlined />
          </Radio.Button>
          <Radio.Button value="b">
            <Popover content={columnSelector} title="" trigger="click">
              <OneToOneOutlined />
              <CaretDownOutlined className="ft12" />
            </Popover>
          </Radio.Button>
        </Radio.Group>
      </Flex>

      <CustomTable
        rowKey="id"
        tableLoading={tableLoading}
        columns={displayColumns}
        tableList={tableList}
        pagination={pagination}
        handleTableChange={handleTableChange}
        getTableListFn={fetchData}
        setTableHead={(patch) => {
          const next =
            typeof patch === "function" ? patch(queryRef.current) : patch;
          queryRef.current = next;
        }}
      />
    </div>
  );
}
